<script>
import partnerChoices from '@/mixins/partnerChoices';
import { validationMixin } from "vuelidate";
import { required ,requiredIf} from "vuelidate/lib/validators";
import agreementValidation from '@/mixins/agreementValidation'
import {hasEditPermission,currentRoleLevelName,trialActiveForClients} from '@/mixins/roleLevelPermissions';
import { mapState } from 'vuex';

export default {
	mixins: [partnerChoices ,validationMixin,agreementValidation],
	props: ['agreementData'],
	methods:{
		validateState(name) {
			const { $dirty, $error } = this.$v.agreementData.relationship[name];
			return $dirty ? !$error : null;
		},
		triggerValidation(){
			this.$v.$touch();
			this.$refs['relationship-cohabited-ref'].triggerValidation();
			this.$refs['relationship-married-ref'].triggerValidation();
			if(this.agreementData.relationship.married){
				this.$refs['location-of-marriage-ref'].triggerValidation();
			}
			if(this.agreementData.relationship.cohabited){
				this.$refs['relationship-living-separately-ref'].triggerValidation()
			}
	    },
	},
	data(){
		return{
			isViewMode : !hasEditPermission('section-basics'),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		}
	},
	computed: {
		...mapState(['currentRoleLevel']),
		relationship() {
			return this.agreementData.relationship;
		},
		clientUserAgreement(){
			return trialActiveForClients() || currentRoleLevelName === 'client-sharing-user';
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		yesNoOptions(){
			return [
				{ value: true, text: 'Yes' },
				{ value: false, text: 'No' },
			]
		},
		// The pension and CPP clauses read it, so a married couple who never cohabited
		// needs one too.
		needsSeparationDate(){
			return this.relationship.cohabited || this.relationship.married
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					cohabited: "Did you live together?",
					married: "Are you married?",
					cohabitationDate: "On what date did you move in together?",
					marriageDate:"On what date did you get married?",
					marriageLocation:"Where did you get married?",
					separationDate:"When did you separate?",
					livingTogether: "Have you been living together after the decision to separate?"
				}
			}
			else{
				return {
					cohabited: "Did the parties cohabit?",
					married: "Are the parties married?",
					cohabitationDate: "What was the date on which cohabitation commenced?",
					marriageDate:"Date on which the parties were married?",
					marriageLocation:"Location of marriage",
					separationDate:"Date on which the parties separated",
					livingTogether: "Have the parties been living separately in the same residence?"
				}
			}
		}
	},
	validations:{
		agreementData:{
			relationship:{
				cohabited:{
					required
				},
				married:{
					required
				},
				cohabitationDate:{
					required: requiredIf(function () {
						return this.agreementData.relationship.cohabited
					})
				},
				marriageDate:{
					required: requiredIf(function () {
						return this.agreementData.relationship.married
					})
				},
				marriageLocation:{
					required: requiredIf(function () {
						return this.agreementData.relationship.married
					})
				},
				separationDate:{
					required: requiredIf(function () {
						return this.agreementData.relationship.cohabited || this.agreementData.relationship.married
					})
				},
				livingTogether:{
					required: requiredIf(function () {
						return this.agreementData.relationship.cohabited
					})
				}
			}
		}
	}
};
</script>

<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Relationship</h4>
			<a href="#" onclick="Intercom('showArticle', 11106501)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>

		<!-- radio-group, not yes-no-choice: the latter writes false back for an
		     unanswered value, and this must stay unanswered until answered. -->
		<radio-group
			v-model="$v.agreementData.relationship.cohabited.$model"
			class="mb-3"
			input-cols="8"
			select
			:label="dynamicQuestions.cohabited"
			:options="yesNoOptions"
			:testid="'relationship-cohabited'"
			:validation="true"
			ref="relationship-cohabited-ref"
			:length="'long'"
			:disabled="isViewMode"
			toolTipText="Cohabiting means living together in a marriage-like relationship.
			How long you lived together decides whether the law treats you as spouses or common-law partners,
			and the answer differs by province, so we ask for the dates rather than asking you to judge it."
		/>

		<conditional-group
			class="my-3"
			:condition="agreementData.relationship.cohabited"
		>
			<date-input
				v-model="$v.agreementData.relationship.cohabitationDate.$model"
				:class="{'mb-3': !$v.agreementData.relationship.cohabitationDate.$error}"
				:label="dynamicQuestions.cohabitationDate"
				:testid="'relationship-cohabitation-date'"
				:disabled="isViewMode"
				:isDateFilled="validateState('cohabitationDate')"
				:isValidationOn="true"
			/>
			<div
				v-if="$v.agreementData.relationship.cohabitationDate.$error"
				class="invalid-feedback d-block col-12"
			>
				This is a required field.
			</div>
		</conditional-group>

		<radio-group
			v-model="$v.agreementData.relationship.married.$model"
			class="mb-3"
			input-cols="8"
			select
			:label="dynamicQuestions.married"
			:options="yesNoOptions"
			:testid="'relationship-married'"
			:validation="true"
			ref="relationship-married-ref"
			:length="'long'"
			:disabled="isViewMode"
			toolTipText="Answer Yes only if the parties are legally married to each other.
			A long common-law relationship is not a marriage, even where the law gives it similar rights."
		/>
		<!-- <conditional-group
			v-if="clientUserAgreement"
			class="my-3"
			:condition="
				relationship.status !== 'Cohabited for under two years with Children'
			"
		>
			<date-input
				v-model="$v.agreementData.relationship.cohabitationDate.$model"
				:class="{'mb-3': !$v.agreementData.relationship.cohabitationDate.$error}"
				label="When did you start living together?"
				:testid="'relationship-cohabitation-date'"
				:isDateFilled="validateState('cohabitationDate')"
				:disabled="isViewMode"
				:isValidationOn="true"
			/>
			<div
				v-if="$v.agreementData.relationship.cohabitationDate.$error"
				class="invalid-feedback d-block col-12"
			>
				This is a required field.
			</div>
			<date-input
				v-if="agreementData.relationship.status === 'Married'"
				v-model="$v.agreementData.relationship.marriageDate.$model"
				:class="{'mb-3': !$v.agreementData.relationship.marriageDate.$error}"
				label="When did you get married?"
				:testid="'relationship-marriage-date'"
				:isDateFilled="validateState('marriageDate')"
				:isValidationOn="true"
				:disabled="isViewMode"
			/>
			<div
				v-if="$v.agreementData.relationship.marriageDate.$error && agreementData.relationship.status === 'Married'"
				class="invalid-feedback d-block col-12"
			>
				This is a required field.
			</div>
			<input-group
				v-if="agreementData.relationship.status === 'Married'"
				v-model="$v.agreementData.relationship.marriageLocation.$model"
				class="mb-3"
				input-cols="8"
				label="Where did you get married?"
				:testid="'relationship-marriage-location'"
				:disabled="isViewMode"
				toolTipText="Name the City and Province or Country (such as Toronto, Ontario or Los Angeles, California or Paris, France"
				:isValidationOn="true"
				ref="location-of-marriage-ref"
			/>

			<date-input
				v-model="$v.agreementData.relationship.separationDate.$model"
				:class="{'mb-3': !$v.agreementData.relationship.separationDate.$error}"
				label="When did you separate?"
				:testid="'relationship-separation-date'"
				:isDateFilled="validateState('separationDate')"
				:isValidationOn="true"
				:disabled="isViewMode"
				toolTipText="This is sometimes tricky to determine because there is often not a clear date.
				Generally speaking, it is the date when you and your spouse begin living separate and apart (whether in the same house or not) with the intention of ending the relationship.
				This can be when one person communicated this intention to the other, when you and your spouse agreed to separate, or when you started telling friends and family about your separation.
				For further information, see the Dates of the Relationship Video in the Education Panel in this Section."
			/>
			<div
				v-if="$v.agreementData.relationship.separationDate.$error"
				class="invalid-feedback d-block col-12"
			>
				This is a required field.
			</div>
			<yes-no-choice
				v-model="agreementData.relationship.livingTogether"
				:class="{'mb-3': !$v.agreementData.relationship.livingTogether.$error}"
				label="Have you been living together in the same residence since your separation?"
				:testid="'relationship-living-separately'"
				:disabled="isViewMode"
				toolTipText="In Canadian family law, the date of separation is not dependent on the physical separation of you and your spouse.
				The key factor is the intention to end the relationship and live separate lives.
				This means that even if you and your spouse continue to live under the same roof,
				your may be still be considered separated if you no longer have a relationship as spouses.
				We will add this acknowledgment into the wording of your agreement."
				:isValidationOn="true"
				ref="relationship-living-separately-ref"
			/>
		</conditional-group> -->

		<conditional-group
			class="my-3"
			:condition="agreementData.relationship.married"
		>
			<date-input
				v-model="$v.agreementData.relationship.marriageDate.$model"
				:class="{'mb-3': !$v.agreementData.relationship.marriageDate.$error}"
				:label="dynamicQuestions.marriageDate"
				:testid="'relationship-marriage-date'"
				:disabled="isViewMode"
				:isDateFilled="validateState('marriageDate')"
				:isValidationOn="true"
			/>
			<div
				v-if="$v.agreementData.relationship.marriageDate.$error"
				class="invalid-feedback d-block col-12"
			>
				This is a required field.
			</div>
			<input-group
				v-model="$v.agreementData.relationship.marriageLocation.$model"
				class="mb-3"
				input-cols="8"
				:label="dynamicQuestions.marriageLocation"
				:testid="'relationship-marriage-location'"
				:disabled="isViewMode"
				:isValidationOn="true"
				ref="location-of-marriage-ref"
				toolTipText="Include either 'City, Province' or 'City, Country' (Toronto, Ontario or Los Angeles, California or Paris, France)"
			/>
		</conditional-group>

		<conditional-group class="my-3" :condition="needsSeparationDate">
			<date-input
				v-model="$v.agreementData.relationship.separationDate.$model"
				:class="{'mb-3': !$v.agreementData.relationship.separationDate.$error}"
				:label="dynamicQuestions.separationDate"
				:testid="'relationship-separation-date'"
				:disabled="isViewMode"
				:isDateFilled="validateState('separationDate')"
				:isValidationOn="true"
				toolTipText="This is sometimes tricky to determine because there is often not a clear date.
				Generally speaking, it is the date when you and your spouse begin living separate and apart (whether in the same house or not) with the intention of ending the relationship.
				This can be when one person communicated this intention to the other, when you and your spouse agreed to separate, or when you started telling friends and family about your separation.
				For further information, see 'Dates of the Relationship' in the Knowledge Base in this section."
				/>
			<div
				v-if="$v.agreementData.relationship.separationDate.$error"
				class="invalid-feedback d-block col-12"
			>
				This is a required field.
			</div>
		</conditional-group>

		<conditional-group
			class="my-3"
			:condition="agreementData.relationship.cohabited"
		>
			<yes-no-choice
				v-model="agreementData.relationship.livingTogether"
				:class="{'mb-3': !$v.agreementData.relationship.livingTogether.$error}"
				:label="dynamicQuestions.livingTogether"
				:testid="'relationship-living-separately'"
				:disabled="isViewMode"
				:isValidationOn="true"
				ref="relationship-living-separately-ref"
				toolTipText="In Canadian family law, the date of separation is not dependent on the physical separation of you and your spouse.
				The key factor is the intention to end the relationship and live separate lives.
				This means that even if you and your spouse continue to live under the same roof,
				you may be still be considered separated if you no longer have a relationship as spouses.
				We will add this acknowledgment into the wording of your agreement."
			/>
		</conditional-group>

		<!-- <conditional-group
			class="my-3"
			:condition="relationship.status === 'Married'"
		>
			<b-form-group
				id="input-divorceApplication"
				input-cols="8"
				label="Which party will apply for divorce?"
				label-for="divorceApplication"
			>
				<b-form-select
					id="divorceApplication"
					v-model="relationship.divorceApplication"
					size="sm"
					:options="[
						...partnerChoices,
						{ text: 'Either', value: 'either' },
					]"
					:data-testid="'relationship-divorce-application'"
				/>
			</b-form-group>

			<b-form-group
				id="input-divorcePayment"
				input-cols="8"
				label="Who will pay for the divorce application?"
				label-for="divorcePayment"
			>
				<b-form-select
					id="divorcePayment"
					v-model="relationship.divorcePayment"
					size="sm"
					:options="[
						...partnerChoices,
						{
							text: 'The parties will share the cost',
							value: 'shared',
						},
					]"
					:data-testid="'relationship-divorce-payment'"
				/>
			</b-form-group>
		</conditional-group> -->

		<additional-clauses v-if='!clientUserAgreement' v-model="agreementData.relationship.additionalClauses" :testid="'relationship-add-clause-'"  :disabled="isViewMode"/>
	</b-card>
</template>
